import React, { useState, useEffect } from 'react';
import { RealtimeDBService } from '../../firebase/unifiedDataService';
import { Logger } from '../../utils/logger';

const log = Logger.create('QuizStudentAccessSettings');

/**
 * Default student access settings for a quiz
 */
export const DEFAULT_STUDENT_ACCESS = {
  visibility: 'visible',        // visible | hidden | after_completion
  allowPractice: true,          // Allow students to practice
  practiceAfterSession: false,  // Only after live session completed
  showCorrectAnswers: 'after_submit',  // never | after_submit | after_quiz
  showExplanations: true        // Show explanations in practice
};

/**
 * Visibility options with labels and descriptions
 */
export const VISIBILITY_OPTIONS = [
  {
    value: 'visible',
    label: 'Sichtbar',
    description: 'Studierende können das Quiz in der Kursübersicht sehen',
    icon: '👁️'
  },
  {
    value: 'hidden',
    label: 'Versteckt',
    description: 'Quiz ist nur für Lehrende sichtbar',
    icon: '🙈'
  },
  {
    value: 'after_completion',
    label: 'Nach Teilnahme',
    description: 'Sichtbar nachdem der Studierende teilgenommen hat',
    icon: '🔓'
  }
];

/**
 * Answer visibility options
 */
export const ANSWER_VISIBILITY_OPTIONS = [
  {
    value: 'never',
    label: 'Nie',
    description: 'Korrekte Antworten werden nie angezeigt'
  },
  {
    value: 'after_submit',
    label: 'Nach Abgabe',
    description: 'Nach jeder beantworteten Frage'
  },
  {
    value: 'after_quiz',
    label: 'Nach Quiz',
    description: 'Erst am Ende des gesamten Quiz'
  }
];

/**
 * QuizStudentAccessSettings - Component to manage quiz visibility and practice settings
 * 
 * @param {Object} props
 * @param {string} props.userId - Teacher's user ID
 * @param {string} props.templateId - Quiz template ID
 * @param {Object} props.currentSettings - Current studentAccess settings
 * @param {Function} props.onUpdate - Callback when settings are updated
 * @param {boolean} props.compact - Use compact layout
 */
export default function QuizStudentAccessSettings({
  userId,
  templateId,
  currentSettings = {},
  onUpdate,
  compact = false
}) {
  const [settings, setSettings] = useState({
    ...DEFAULT_STUDENT_ACCESS,
    ...currentSettings
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setSettings({
      ...DEFAULT_STUDENT_ACCESS,
      ...currentSettings
    });
  }, [currentSettings]);

  const handleChange = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    // Auto-save using RealtimeDBService
    if (userId && templateId) {
      setSaving(true);
      try {
        await RealtimeDBService.update(`quiz_templates/${userId}/${templateId}`, {
          studentAccess: newSettings,
          lastModified: Date.now()
        });
        
        if (onUpdate) {
          onUpdate(newSettings);
        }
        
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch (err) {
        log.error('Error saving student access settings', err);
      } finally {
        setSaving(false);
      }
    }
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-4 text-sm">
        {/* Visibility Badge */}
        <div className="flex items-center space-x-1">
          <span className="text-gray-500">Sichtbarkeit:</span>
          <select
            value={settings.visibility}
            onChange={(e) => handleChange('visibility', e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {VISIBILITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.icon} {opt.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Practice Toggle */}
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.allowPractice}
            onChange={(e) => handleChange('allowPractice', e.target.checked)}
            className="rounded text-blue-600"
          />
          <span className="text-gray-700">Übungsmodus</span>
        </label>
        
        {/* Save indicator */}
        {saving && <span className="text-gray-400 text-xs">Speichert...</span>}
        {saved && <span className="text-green-600 text-xs">✓ Gespeichert</span>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Studierenden-Zugang
        </h3>
        {saving && (
          <span className="text-sm text-gray-500">Speichert...</span>
        )}
        {saved && (
          <span className="text-sm text-green-600">✓ Gespeichert</span>
        )}
      </div>

      <div className="space-y-6">
        {/* Visibility Setting */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sichtbarkeit für Studierende
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {VISIBILITY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleChange('visibility', opt.value)}
                className={`
                  p-4 rounded-lg border-2 text-left transition-all
                  ${settings.visibility === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xl">{opt.icon}</span>
                  <span className="font-medium text-gray-900">{opt.label}</span>
                </div>
                <p className="text-xs text-gray-500">{opt.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Practice Settings */}
        <div className="border-t border-gray-200 pt-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Übungsmodus
          </label>
          
          <div className="space-y-3">
            {/* Allow Practice */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.allowPractice}
                onChange={(e) => handleChange('allowPractice', e.target.checked)}
                className="mt-1 rounded text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900">Üben erlauben</span>
                <p className="text-sm text-gray-500">
                  Studierende können das Quiz selbstständig wiederholen
                </p>
              </div>
            </label>

            {/* Practice after session only */}
            {settings.allowPractice && (
              <label className="flex items-start space-x-3 cursor-pointer ml-6">
                <input
                  type="checkbox"
                  checked={settings.practiceAfterSession}
                  onChange={(e) => handleChange('practiceAfterSession', e.target.checked)}
                  className="mt-1 rounded text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Nur nach Live-Session</span>
                  <p className="text-sm text-gray-500">
                    Übung erst möglich nachdem eine Live-Session stattfand
                  </p>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Answer Visibility */}
        <div className="border-t border-gray-200 pt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Korrekte Antworten anzeigen
          </label>
          <select
            value={settings.showCorrectAnswers}
            onChange={(e) => handleChange('showCorrectAnswers', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ANSWER_VISIBILITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label} - {opt.description}
              </option>
            ))}
          </select>
        </div>

        {/* Show Explanations */}
        <div className="border-t border-gray-200 pt-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showExplanations}
              onChange={(e) => handleChange('showExplanations', e.target.checked)}
              className="mt-1 rounded text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="font-medium text-gray-900">Erklärungen anzeigen</span>
              <p className="text-sm text-gray-500">
                Zeige Erklärungen zu Fragen im Übungsmodus (falls vorhanden)
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

/**
 * Get student access settings for a quiz template
 */
export async function getStudentAccessSettings(userId, templateId) {
  try {
    const studentAccess = await RealtimeDBService.get(`quiz_templates/${userId}/${templateId}/studentAccess`);
    
    if (studentAccess) {
      return { ...DEFAULT_STUDENT_ACCESS, ...studentAccess };
    }
    return DEFAULT_STUDENT_ACCESS;
  } catch (err) {
    log.error('Error getting student access settings', err);
    return DEFAULT_STUDENT_ACCESS;
  }
}

/**
 * Check if a quiz is visible to a specific student
 */
export function isQuizVisibleToStudent(studentAccess, hasParticipated = false) {
  if (!studentAccess) return true;
  
  switch (studentAccess.visibility) {
    case 'hidden':
      return false;
    case 'after_completion':
      return hasParticipated;
    case 'visible':
    default:
      return true;
  }
}

/**
 * Check if practice mode is available for student
 */
export function canStudentPractice(studentAccess, hasLiveSessionCompleted = false) {
  if (!studentAccess) return true;
  if (!studentAccess.allowPractice) return false;
  if (studentAccess.practiceAfterSession && !hasLiveSessionCompleted) return false;
  return true;
}
